"use client";

/**
 * Shared hook for search-with-infinite-scroll modals.
 * Manages search term, initial list, search results, loading states, and infinite scroll.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { MODAL_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "../_logic/constants";

/** Result shape expected from list and search server actions. */
interface ActionResult<T> {
  success: boolean;
  data?: T[];
}

interface UseSearchableModalListOptions<T> {
  /** Whether the modal is currently open. Resets state on close, loads data on open. */
  isOpen: boolean;
  /** Server action to list recent items with offset pagination. */
  listAction: (offset?: number) => Promise<ActionResult<T>>;
  /** Server action to search items by term. */
  searchAction: (term: string) => Promise<ActionResult<T>>;
  /** Optional callback to run in parallel with initial load (e.g., fetching existing assignments). */
  onOpen?: () => Promise<void>;
}

export interface UseSearchableModalListReturn<T> {
  /** Current search term. */
  searchTerm: string;
  /** Update the search term. */
  setSearchTerm: (term: string) => void;
  /** Items to display — search results when searching, initial items otherwise. */
  displayItems: T[];
  /** True during initial data load on open. */
  isLoading: boolean;
  /** True during debounced search. */
  isSearching: boolean;
  /** True while loading the next page via infinite scroll. */
  isLoadingMore: boolean;
  /** Whether there are more items to load. */
  hasMore: boolean;
  /** Ref to attach to the infinite scroll sentinel element. */
  sentinelRef: React.RefObject<HTMLDivElement>;
}

/**
 * Manages the common search + infinite scroll pattern used across modals.
 * Handles debounced search, pagination, loading states, and cleanup on close.
 * @param options - Configuration for list/search actions and modal state
 * @returns Search state and refs for rendering the modal list
 */
export function useSearchableModalList<T>({
  isOpen,
  listAction,
  searchAction,
  onOpen,
}: UseSearchableModalListOptions<T>): UseSearchableModalListReturn<T> {
  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [initialItems, setInitialItems] = useState<T[]>([]);
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Refs for callback stability — avoids stale closures in effects
  const listActionRef = useRef(listAction);
  const searchActionRef = useRef(searchAction);
  const onOpenRef = useRef(onOpen);
  listActionRef.current = listAction;
  searchActionRef.current = searchAction;
  onOpenRef.current = onOpen;

  // Derived values
  const displayItems = searchTerm.trim() ? searchResults : initialItems;

  // Effects — reset state and load initial data when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setInitialItems([]);
      setSearchResults([]);
      setHasMore(true);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const promises: Promise<void>[] = [
          listActionRef.current(0).then((result) => {
            if (!cancelled && result.success && result.data) {
              setInitialItems(result.data);
              setHasMore(result.data.length >= MODAL_PAGE_SIZE);
            }
          }),
        ];
        if (onOpenRef.current) {
          promises.push(onOpenRef.current());
        }
        await Promise.all(promises);
      } catch {
        // Silently handle — individual actions log their own errors
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Effects — debounced search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await searchActionRef.current(searchTerm);
        if (result.success && result.data) {
          setSearchResults(result.data);
        }
      } catch {
        // Silently handle
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Effects — infinite scroll for initial list
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || searchTerm.trim() || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsLoadingMore(true);
          listActionRef
            .current(initialItems.length)
            .then((result) => {
              if (result.success && result.data) {
                setInitialItems((prev) => [...prev, ...result.data!]);
                if (result.data.length < MODAL_PAGE_SIZE) setHasMore(false);
              }
            })
            .finally(() => setIsLoadingMore(false));
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [searchTerm, hasMore, isLoadingMore, initialItems.length]);

  return {
    searchTerm,
    setSearchTerm,
    displayItems,
    isLoading,
    isSearching,
    isLoadingMore,
    hasMore,
    sentinelRef,
  };
}
